import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';

import { TenantUserService } from '../../services/tenant-user.service';
import { CreateAgentRequest } from '../../models/customer.model';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { StatusPillComponent } from '../../components/status-pill/status-pill.component';
import { KiKontoPillComponent } from '../../components/ki-konto-pill/ki-konto-pill.component';
import { FIELD_LENGTHS } from '../../shared/field-lengths';

const FIRST_LAST_NAME_MAX = 100; // Backend-@Size
const KUERZEL_PATTERN = /^[a-z0-9-]*$/;

@Component({
  selector: 'app-agent-create-page',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    AvatarComponent,
    StatusPillComponent,
    KiKontoPillComponent,
  ],
  templateUrl: './agent-create-page.component.html',
  styleUrl: './agent-create-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentCreatePageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly tenantUserService = inject(TenantUserService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly customerId = this.route.snapshot.paramMap.get('customerId') ?? '';

  readonly fieldLengths = { FIRST_LAST_NAME_MAX, KUERZEL_MAX: FIELD_LENGTHS.KUERZEL_MAX };
  readonly saving = signal(false);

  readonly form = this.fb.group({
    kuerzel: this.fb.control('', [
      Validators.required,
      Validators.minLength(FIELD_LENGTHS.KUERZEL_MIN),
      Validators.maxLength(FIELD_LENGTHS.KUERZEL_MAX),
      Validators.pattern(KUERZEL_PATTERN),
    ]),
    firstName: this.fb.control('', [Validators.required, Validators.maxLength(FIRST_LAST_NAME_MAX)]),
    lastName: this.fb.control('', [Validators.required, Validators.maxLength(FIRST_LAST_NAME_MAX)]),
  });

  private readonly formValue = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });
  private readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });

  readonly previewName = computed(() => {
    const { firstName, lastName } = this.formValue();
    return `${firstName ?? ''} ${lastName ?? ''}`.trim();
  });
  readonly previewKuerzel = computed(() => (this.formValue().kuerzel ?? '').trim());
  readonly canSubmit = computed(() => this.formStatus() === 'VALID' && !this.saving());

  onSubmit(): void {
    if (this.form.invalid || this.saving()) return;
    this.saving.set(true);
    const { kuerzel, firstName, lastName } = this.form.getRawValue();
    const request: CreateAgentRequest = {
      kuerzel: kuerzel.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    };

    this.tenantUserService
      .createAgent(this.customerId, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.snackBar.open(this.translate.instant('agentCreate.success'), undefined, {
            panelClass: 'snackbar-success',
          });
          this.navigateBack();
        },
        error: (err: HttpErrorResponse) => {
          this.saving.set(false);
          const code = (err.error as { code?: string } | null)?.code;
          if (err.status === 409) {
            this.markKuerzelError('taken');
          } else if (err.status === 400 && code === 'INVALID_KUERZEL') {
            this.markKuerzelError('invalidKuerzel');
          } else {
            this.snackBar.open(this.translate.instant('agentCreate.error'), undefined, {
              panelClass: 'snackbar-error',
            });
          }
        },
      });
  }

  // Serverfehler gelten nur für den abgeschickten Wert; die nächste Eingabe prüfen wieder die Validatoren.
  private markKuerzelError(error: 'taken' | 'invalidKuerzel'): void {
    this.form.controls.kuerzel.setErrors({ [error]: true });
    this.form.controls.kuerzel.markAsTouched();
    this.cdr.markForCheck();
  }

  navigateBack(): void {
    void this.router.navigate(['/client-management', this.customerId]);
  }
}
